import { z } from "zod";
import type { MedplumGender } from "@/models/member/Member";

export const MedmoUpdateTypes = {
  STAGE_CHANGED: 1,
  NEW_FILE_UPLOADED: 2,
  NEW_REPORT_UPLOADED: 3,
  NEW_COMMENT_ADDED: 4,
  APPOINTMENT_INFO_UPDATED: 5,
} as const;

export type MedmoUpdateTypeId = (typeof MedmoUpdateTypes)[keyof typeof MedmoUpdateTypes];

// Define Medmo stage IDs
export const MedmoStages = {
  CANCELLED: 0,
  REQUESTED: 1,
  IN_PROGRESS: 2,
  SCHEDULED: 3,
  COMPLETED: 4,
  ON_HOLD: 6,
} as const;

// Create a type for stage IDs
export type MedmoStageId = (typeof MedmoStages)[keyof typeof MedmoStages];

/** Narrows a raw Medmo stage ID to one we have a FHIR mapping for. */
export function isKnownMedmoStage(stageId: number): stageId is MedmoStageId {
  return Object.values(MedmoStages).includes(stageId as MedmoStageId);
}

/**
 * Medmo's inbound webhook envelope.
 *
 * `UpdateTypeID` and the stage IDs are *open* integer vocabularies on Medmo's
 * side — their OpenAPI spec types `StageID` as a bare integer and they add
 * values without notice. Validating them against our closed enums rejected the
 * whole delivery with a 400 for a value we merely didn't recognize, and because
 * Medmo stamps the order's current stage onto *every* webhook it sends, one
 * unmapped stage silently killed that order's comments, files, and radiology
 * reports too. Accept the envelope; let the route and the jobs decide what they
 * can act on.
 *
 * Stage fields ride along only on stage-bearing update types — Medmo omits them
 * entirely on others — so they're optional here and required by
 * {@link MedmoWebhookWithStageSchema} for the jobs that can't act without them.
 */
export const MedmoWebhookSchema = z.object({
  MedmoOrderID: z.number().int().positive(),
  UpdateTypeID: z.number().int(),
  UpdateType: z.string(),
  UpdateDescription: z.string(),
  ExternalOrderID: z.string().nullable().optional(),
  OriginalStageID: z.number().int().optional(),
  OriginalStage: z.string().optional(),
  NewStageID: z.number().int().optional(),
  NewStage: z.string().optional(),
  FileID: z.number().int().positive().nullable().optional(),
});

export type MedmoWebhookEvent = z.infer<typeof MedmoWebhookSchema>;

/**
 * A delivery that carries the stage snapshot. Required by the stage-changed and
 * appointment-info jobs, which read `NewStageID` to derive FHIR status.
 */
export const MedmoWebhookWithStageSchema = MedmoWebhookSchema.extend({
  OriginalStageID: z.number().int(),
  OriginalStage: z.string(),
  NewStageID: z.number().int(),
  NewStage: z.string(),
});

export type MedmoWebhookWithStageEvent = z.infer<typeof MedmoWebhookWithStageSchema>;

export const MEDMO_IDENTIFIER_SYSTEM_LOCATIONS = "medmo.locations";
export const MEDMO_IDENTIFIER_SYSTEM_PRESCRIBERS = "medmo.prescribers";
export const MEDMO_IDENTIFIER_SYSTEM_ORDERS = "medmo.orders";
export const MEDMO_IDENTIFIER_SYSTEM_APPOINTMENTS = "medmo.appointments";
export const MEDMO_IDENTIFIER_SYSTEM_COMMENTS = "medmo.comments";

export interface MedmoLocation {
  MedmoLocationID: number;
  Name: string;
  Address1: string;
  Address2?: string;
  City: string;
  State: string;
  ZipCode: string;
  Phone: string;
  PhoneExt: string;
  Fax: string;
  TaxID?: string | null;
  ExternalLocationID: string;
}

export interface MedmoPrescriber {
  MedmoPrescriberID: number;
  NPI: string;
  FirstName: string;
  LastName: string;
  SpecialityID: number;
  Speciality?: string;
  ExternalPrescriberID: string;
  Phone?: string | null;
  Fax?: string | null;
}

export interface MedmoSpeciality {
  MedmoSpecialityID: number;
  Speciality: string;
}

export interface MedmoScan {
  MedmoScanID: number;
  FullScanName: string;
  Modality: string;
  BodyPart: string;
  Option1: string;
  Option2: string;
  CPTCode: number;
}

export interface MedmoPatient {
  MedmoPatientID: number;
  FirstName: string;
  LastName: string;
  DOB: string;
  Gender: MedmoGenderString;
  PreferredLanguage: "English" | "Spanish" | "Others";
  MRN: string;
  Email: string;
  Phone: string; // Preferred format (###)###-####
  Address1: string;
  Address2: string;
  City: string;
  State: string; // Two-letter state code
  ZipCode: string;
  InsurancePlan: string;
  PolicyNumber: string;
  GroupNumber: string;
  ExternalPatientID: string;
}

// Verified against Medmo's `GET /genders` lookup (GenderIDs 1-4).
type MedmoGenderString = "Female" | "Male" | "Other" | "I prefer not to say";

export interface MedmoGender {
  GenderID: number;
  Gender: MedmoGenderString;
}

export interface MedmoLanguage {
  PreferredLanguageID: number;
  PreferredLanguage: string;
}

export interface MedmoOrder {
  MedmoOrderID: number;
  ExternalOrderID?: string;
  MedmoPatientID: number;
  ExternalPatientID: string;
  MedmoPrescriberID: number;
  ExternalPrescriberID: string;
  MedmoLocationID: number;
  ExternalLocationID: string;
  MedmoScanID: number;
  FullScanName: string;
  Modality: string;
  BodyPart: string;
  Option1?: string;
  Option2?: string;
  CPTCode: number;
  ICD10?: string;
  OwnerID?: number;
  ExamReason?: string;
  SchedulingNotes?: string;
  AdditionalNotes?: string;
  InsurancePlan?: string;
  PolicyNumber?: string;
  GroupNumber?: string;
  StageID: number;
  Stage: string;
  DateRequested: string;
  RxFileCount: number;
  ClinicalNoteFileCount: number;
  ReportFileCount: number;
  PAFileCount: number;
  OnHoldInfo?: {
    OnHoldReason: string;
    OnHoldExternalReasonOther: string;
  };
  CloseInfo?: {
    CloseReason?: string;
    CloseReasonDescription?: string;
  };
  AppointmentInformation?: MedmoAppointmentInformation;
  Files: Omit<MedmoFile, "FileUrl">[];
  Comments: MedmoComment[];
}

export interface MedmoAppointmentInformation {
  AppointmentID: number;
  AppointmentDate: string;
  PracticeName: string;
  PracticeLocationName: string;
  PracticeLocationTimeZone: string;
  PracticeLocationAddress1: string;
  PracticeLocationAddress2?: string | null;
  PracticeLocationCity: string;
  PracticeLocationState: string;
  PracticeLocationZipCode: string;
}

/**
 * Medmo rejects an imaging order that carries more than this many ICD-10 diagnosis
 * codes. Enforced app-side so providers get a clear error before submission rather
 * than an opaque vendor rejection (ENG-4761).
 */
export const MEDMO_MAX_ICD10_CODES = 5;

export interface CreateMedmoOrderRequest {
  MedmoPatientID: number;
  ExternalPatientID: string;
  MedmoPrescriberID: number;
  ExternalPrescriberID: string;
  MedmoLocationID: number;
  ExternalLocationID: string;
  MedmoScanID: number;
  ICD10?: string;
  OwnerID?: number;
  ExamReason: string;
  SchedulingNotes?: string;
  AdditionalNotes?: string;
  InsurancePlan: string;
  PolicyNumber: string;
  GroupNumber: string;
  ZipCode: string;
  ExternalOrderID: string;
  // Optional per Medmo's spec. We intentionally don't set it: clinical notes are
  // sent as an uploaded PDF file (see on-order-created), not inline on the order.
  ClinicalNotes?: string | null;
}

export const MedmoCommentTypes = {
  CLIENT_URGENT: 1,
  CLIENT_STANDARD: 2,
  MEDMO: 3,
} as const;

export type MedmoCommentTypeID = (typeof MedmoCommentTypes)[keyof typeof MedmoCommentTypes];

export interface MedmoComment {
  CommentID: number;
  CommentTypeID: MedmoCommentTypeID;
  CommentType: string;
  Comment: string;
  LastUpdated: string;
}

export interface CreateMedmoCommentRequest {
  CommentText: string;
  IsUrgent: boolean;
}

export interface AddReportFileRequest {
  FileType: "PDF" | "PNG";
}

export interface ManualOrderUpdateResponse {
  MFPStageName?: string;
  ActiveReportFiles?: number;
  ReportList?: string;
  HasAppointment?: number;
  ICLocationName?: string;
  ICAddress1?: string;
  ICCity?: string;
  ICState?: string;
  ICZipCode?: string;
  WindowOpen?: string;
  AppointmentDateTime?: string;
  ICTimeZone?: string;
}

export interface ChangeOrderStageRequest {
  Stage: "Requested" | "In-Progress" | "Scheduled" | "Completed" | "On-Hold" | "Cancelled";
  ICRegion?: "NYC" | "CHI" | "LA";
  AppointmentDateTime?: string;
}

export const MedmoFileTypes = {
  RX: 1,
  CLINICAL_NOTE: 2,
  POST_SCAN_REPORT: 3,
  PRIOR_AUTHORIZATION: 8,
} as const;

export type MedmoFileTypeID = (typeof MedmoFileTypes)[keyof typeof MedmoFileTypes];

export interface MedmoFile {
  FileID: number;
  FileTitle?: string;
  FileTypeID: MedmoFileTypeID;
  FileType: string;
  LastUpdated?: string;
  // Present on the file-listing endpoints; not part of the embedded Order.Files
  // schema, which is why Order.Files below uses Omit<MedmoFile, "FileUrl">.
  FileUrl: string;
}

export interface MedmoFileType {
  FileTypeID: number;
  FileTypeName: string;
}

// Maps Medplum's administrative-gender to Medmo's accepted gender strings,
// verified against Medmo's `GET /genders` lookup. FHIR `unknown` (and an absent
// value) map to Medmo's "I prefer not to say" so imaging orders aren't blocked
// for members without a recorded sex/gender. The exact string matters: Medmo
// rejects near-misses like "Prefer not to say" with a 400.
const MEDMO_GENDER_BY_MEDPLUM_GENDER: Record<MedplumGender, MedmoGenderString> = {
  female: "Female",
  male: "Male",
  other: "Other",
  unknown: "I prefer not to say",
};

export function convertToMedmoGender(gender: MedplumGender | undefined): MedmoGenderString {
  if (!gender) return "I prefer not to say";
  return MEDMO_GENDER_BY_MEDPLUM_GENDER[gender];
}
